import type { OngoingItemAttachmentUploads, PosterFrameProcessingType } from '@mimir/client-common/model/itemAttachment';
import type { AsyncOperationType } from '@mimir/common/dto/asyncOperationType';
import type { TimedCoordinate } from '@mimir/common/dto/GeoLocation';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import type { LiveSourceDtoV2 } from '@mimir/common/dto/liveSourceDtos';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type { RouterDto } from '@mimir/common/dto/routerControllerDtos';
import type { Storages } from '@mimir/common/dto/storageModel';
import type { GetItemUploadLockResponseDto, UploadLocationClientDto } from '@mimir/common/dto/uploadLocationDto';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import type { Filters } from '@mimir/common/filters/Filters';
import type { MosItemReplaceData } from '@mimir/common/mos/mos-interfaces';
import type { Store } from 'vuex';
import type { UploadItem, UploadSummary } from '@mimir/client-common/upload/UploadItem';
import type { LayoutFeatures } from '../model/layoutFeatures';
import type { User } from '../model/User';
import type { ItemEditStatusClientMap } from '../util/itemEditStatusTypes';
import type { UserInterfaceSettings } from '../util/localStorage';
import type { UploadManager } from '../util/UploadManager';
import type { ConfigModuleState } from './config-module-state';
import type { DesktopModuleState } from './desktop-module-state';
import type { ExportModuleState } from './export-module-state';
import type { MdfModuleState } from './mdf-module-state';
import type { SearchModuleState } from './search-module-state';
import type { SearchPlayerModuleState } from './search-player-module-state';
import type { TimelineModuleState } from './timeline-module-state';
import type { PlayoutDevicesHelper } from '../pages/playout/playoutDevicesHelper';
import type { StandardItemEntryDto } from '@mimir/common/dto/itemModels';

export enum MenuItemKeys {
  Filter = 'MenuItem.Filters',
  Folders = 'MenuItem.Folders',

  // TODO: Remove the following two items when the 'newTabsAndCutter' feature flag is removed
  // as they have been moved to the new tabs.
  Activity = 'MenuItem.Activity',
  Scheduler = 'MenuItem.Scheduler',

  Bookmarks = 'MenuItem.Bookmarks',
  Projects = 'MenuItem.Projects',
  Premiere = 'MenuItem.Premiere',
  DVR = 'MenuItem.DVR',
  RecycleBin = 'MenuItem.RecycleBin',
  Share = 'MenuItem.MyShares',
  Tasks = 'MenuItem.MyTasks',
}

type MenuItem = keyof typeof MenuItemKeys;
export type MenuItems = { [V in MenuItem]: LeftMenuItem };

export interface LeftMenuItem {
  icon?: string;
  key: string;
  routePath: string;
  rightIcon?: string;
  iconImagePath?: string;
  onClick?: (store: StoreType) => void;
  onRightIconClick?: () => void;
  childRoutes: string[];
  isVisible: boolean;
  routeQuery?: Record<string, string | boolean> | (() => Record<string, string | boolean>);
}

/**
 * Full interface description of root state and all of the modules.
 */
export interface State {
  appLoaded: boolean,
  wsConnected: boolean,
  mosItemReplaceData: MosItemReplaceData | null,
  backToExport: boolean,
  sideBarItems: MenuItems,
  supportedOps: AsyncOperationType[],
  userInterfaceSettings: UserInterfaceSettings,
  /*
    Note that the meaning of ongoingUploads became a bit confused after the
    introduction of item stitching. The ongoingUploads state essentially array
    tracks file uploads, not root items or ongoing upload operation., however
    parts of the UIs assume that root items, files and upload operations are
    equivalent.

    Newer UIs use appState.getUploadProgress() which specifically addresses the
    state of root-items.

    TODO: Implement operation tracking remove or migrate the UIs that depend on
    ongoingUploads.
  */
  ongoingUploads: UploadItem[],
  itemIdsToPoll: string[],
  selectedUpload: UploadItem | null,
  expectedItemType: DynamoItemType | null,
  selectedFiles: File[],
  user: User,
  allUsers: UserInfoDto[],
  filters: Filters,
  uploadManager: UploadManager,
  storage: Storages,
  liveSources: LiveSourceDtoV2[],
  routers: RouterDto[],
  optionLists: OptionListDto[],
  ongoingFetchOfOptionListIds: Record<string, boolean | undefined>,
  searchMap: {
    zoom: number,
    center: TimedCoordinate,
    isInitialMap: boolean,
  },
  itemUploadLock: GetItemUploadLockResponseDto,
  uploadSummary: UploadSummary | null,
  loadedUploadLocation: UploadLocationClientDto | null,
  preferredUploadStorageKey: string | null,
  freshUploadLocation: boolean,
  preventBrowserNavigationWarning: boolean,
  postMessageOrigin: string, // If set, all postMessage messages will be sent to this origin
  currentPersonName: string, // Used to expose the current opened person name to the bookmark feature.
  ongoingItemAttachmentUploads: OngoingItemAttachmentUploads,
  posterFrameProcessingState: PosterFrameProcessingType,
  itemEditStatusMap: ItemEditStatusClientMap,
  playoutDevicesHelper: PlayoutDevicesHelper,
  lastPolledItems: StandardItemEntryDto[],

  // Module interfaces
  mdf: MdfModuleState,
  timeline: TimelineModuleState,
  desktop: DesktopModuleState,
  export: ExportModuleState,
  searchPlayer: SearchPlayerModuleState,
  search: SearchModuleState,
  config: ConfigModuleState,
  desktopProfileMenuName: string;
  layoutFeatures: LayoutFeatures;
  latestLogId: string;
  thisClientInstanceId: string;

  /**
   *  When true, the UI is in an "experimental mode" where all feature flags
   *  checked using the ClientFeatureSet.prototype.isFeatureEnabled() will be
   *  enabled, unless locally overridden. See isFeatureEnabled() for exact
   *  behavior.
   */
  experimental: boolean;

  dialogCounter: number;

  /**
   * Can be used to force a cursor to a certain CSS cursor property no matter where the user hovers the mouse.
   * Note that you need to call clearMouseCursor to reset it back.
   */
  forceMouseCursor: string | null;
  copiedItemId: string | null;
  notifiedHostingFrame: boolean;
  enableRichPayloads: boolean;
  enableUpdatingMimir: boolean;
  lastCopyLinkItemId: string | null;

  storagesLoaded: boolean;
}

export type StoreType = Store<State>;
